import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type Profile, type Subscription, type RefundRequest, type Inquiry, MASTER_OPERATOR_ID } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  PLANS,
  PLAN_LABELS,
  formatPrice,
  CAPACITY_EXPANSION_PRICE,
  BANK_INFO,
  type PlanKey,
} from '@/lib/plans';
import AdminPasswordReset from './AdminPasswordReset';
import MemberManager from './MemberManager';
import OwnerManager from './OwnerManager';

interface Academy {
  id: string;
  name: string;
  address: string | null;
  owner_id: string;
  max_students: number;
}

interface CapacityReq {
  id: string;
  academy_id: string;
  requested_slots: number;
  price: number;
  status: string;
  created_at: string;
  academies: { name: string } | null;
}

const planKeys: PlanKey[] = ['1month', '3month', '12month'];

export default function OperatorDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [pendingStudents, setPendingStudents] = useState<Profile[]>([]);
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [studentCounts, setStudentCounts] = useState<Record<string, number>>({});
  const [capacityRequests, setCapacityRequests] = useState<CapacityReq[]>([]);
  const [pendingSubs, setPendingSubs] = useState<Subscription[]>([]);
  const [subProfiles, setSubProfiles] = useState<
    Record<string, { full_name: string | null; phone: string | null; academy_name: string | null }>
  >({});
  const [planSel, setPlanSel] = useState<Record<string, PlanKey>>({});
  const [refundReqs, setRefundReqs] = useState<RefundRequest[]>([]);
  const [refundProfiles, setRefundProfiles] = useState<
    Record<string, { full_name: string | null; phone: string | null }>
  >({});
  const [refundBusy, setRefundBusy] = useState<string | null>(null);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [inqProfiles, setInqProfiles] = useState<
    Record<string, { full_name: string | null; phone: string | null }>
  >({});
  const [inqResponses, setInqResponses] = useState<Record<string, string>>({});
  const [inqBusy, setInqBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, academiesRes, studentCountsRes, capacityRes, subsRes, refundRes, inqRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('role', 'student').eq('status', 'pending'),
        supabase.from('academies').select('id, name, address, owner_id, max_students'),
        supabase.from('students').select('academy_id'),
        supabase
          .from('capacity_requests')
          .select('id, academy_id, requested_slots, price, status, created_at, academies(name)')
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),
        supabase
          .from('subscriptions')
          .select('id, user_id, role, plan, price, status, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),
        supabase
          .from('refund_requests')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),
        supabase
          .from('inquiries')
          .select('*')
          .order('created_at', { ascending: false }),
      ]);

      if (studentsRes.error) throw studentsRes.error;
      if (academiesRes.error) throw academiesRes.error;
      if (studentCountsRes.error) throw studentCountsRes.error;
      if (capacityRes.error) throw capacityRes.error;
      if (subsRes.error) throw subsRes.error;
      if (refundRes.error) throw refundRes.error;
      if (inqRes.error) throw inqRes.error;

      setPendingStudents((studentsRes.data as Profile[]) || []);
      setAcademies((academiesRes.data as Academy[]) || []);
      setCapacityRequests((capacityRes.data as CapacityReq[]) || []);
      setPendingSubs((subsRes.data as Subscription[]) || []);

      const subs = (subsRes.data as Subscription[]) || [];
      const subIds = subs.map((s) => s.user_id);
      if (subIds.length > 0) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, full_name, phone, academy_name')
          .in('id', subIds);
        const map: Record<string, { full_name: string | null; phone: string | null; academy_name: string | null }> = {};
        ((profs as Array<{ id: string; full_name: string | null; phone: string | null; academy_name: string | null }>) || []).forEach((p) => {
          map[p.id] = { full_name: p.full_name, phone: p.phone, academy_name: p.academy_name };
        });
        setSubProfiles(map);
      } else {
        setSubProfiles({});
      }

      const sel: Record<string, PlanKey> = {};
      ((subsRes.data as Subscription[]) || []).forEach((s) => {
        sel[s.id] = (s.plan as PlanKey) || '1month';
      });
      setPlanSel(sel);

      const refunds = (refundRes.data as RefundRequest[]) || [];
      setRefundReqs(refunds);
      const refundUserIds = [...new Set(refunds.map((r) => r.user_id))];
      if (refundUserIds.length > 0) {
        const { data: rProfs } = await supabase
          .from('profiles')
          .select('id, full_name, phone')
          .in('id', refundUserIds);
        const rMap: Record<string, { full_name: string | null; phone: string | null }> = {};
        ((rProfs as Array<{ id: string; full_name: string | null; phone: string | null }>) || []).forEach((p) => {
          rMap[p.id] = { full_name: p.full_name, phone: p.phone };
        });
        setRefundProfiles(rMap);
      } else {
        setRefundProfiles({});
      }

      const inqs = (inqRes.data as Inquiry[]) || [];
      setInquiries(inqs);
      const inqUserIds = [...new Set(inqs.map((i) => i.user_id))];
      if (inqUserIds.length > 0) {
        const { data: iProfs } = await supabase
          .from('profiles')
          .select('id, full_name, phone')
          .in('id', inqUserIds);
        const iMap: Record<string, { full_name: string | null; phone: string | null }> = {};
        ((iProfs as Array<{ id: string; full_name: string | null; phone: string | null }>) || []).forEach((p) => {
          iMap[p.id] = { full_name: p.full_name, phone: p.phone };
        });
        setInqProfiles(iMap);
      } else {
        setInqProfiles({});
      }

      // 만료된 이용권 자동 정리
      supabase.rpc('expire_overdue_subscriptions').then(() => undefined).catch(() => undefined);

      const counts: Record<string, number> = {};
      (studentCountsRes.data || []).forEach((s: { academy_id: string | null }) => {
        if (s.academy_id) {
          counts[s.academy_id] = (counts[s.academy_id] || 0) + 1;
        }
      });
      setStudentCounts(counts);
    } catch (e) {
      setError('데이터를 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approveStudent = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('approve_student', { p_profile_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('학생 승인 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const approveExpansion = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('approve_capacity_expansion', { p_request_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('증설 승인 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const rejectExpansion = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('reject_capacity_expansion', { p_request_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('증설 거절 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const approveSubscription = async (id: string) => {
    const plan = planSel[id] || '1month';
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('approve_subscription', {
        p_subscription_id: id,
        p_plan: plan,
      });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('구독 승인 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const rejectSubscription = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('reject_subscription', { p_subscription_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('구독 거절 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const approveRefund = async (id: string) => {
    setRefundBusy(id);
    setError('');
    try {
      const { error: err } = await supabase.rpc('approve_refund', { p_request_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('환불 승인 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setRefundBusy(null);
    }
  };

  const rejectRefund = async (id: string) => {
    setRefundBusy(id);
    setError('');
    try {
      const { error: err } = await supabase.rpc('reject_refund', { p_request_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('환불 거절 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setRefundBusy(null);
    }
  };

  const respondInquiry = async (id: string) => {
    const response = (inqResponses[id] || '').trim();
    if (!response) {
      setError('답변 내용을 입력해주세요.');
      return;
    }
    setInqBusy(id);
    setError('');
    try {
      const { error: err } = await supabase.rpc('respond_inquiry', {
        p_inquiry_id: id,
        p_response: response,
      });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('문의 답변 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setInqBusy(null);
    }
  };

  const reject = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase
        .from('profiles')
        .update({ status: 'rejected' })
        .eq('id', id);
      if (err) throw err;
      setPendingStudents((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError('거절 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const forceDelete = async (id: string) => {
    if (!window.confirm('정말로 이 계정을 강제 탈퇴시키겠습니까? 되돌릴 수 없습니다.')) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('delete_account', { p_user_id: id });
      if (err) throw err;
      setPendingStudents((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError('강제 탈퇴 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const totalStudents = Object.values(studentCounts).reduce((a, b) => a + b, 0);
  const isMasterOperator = profile?.role === 'operator' && profile?.id === MASTER_OPERATOR_ID;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon="ri-building-2-line"
          label="등록된 학원"
          value={`${academies.length}곳`}
          tone="primary"
        />
        <StatCard
          icon="ri-user-smile-line"
          label="전체 학생 수"
          value={`${totalStudents}명`}
          tone="accent"
        />
        <StatCard
          icon="ri-time-line"
          label="승인 대기"
          value={`${pendingStudents.length + pendingSubs.length + refundReqs.length}건`}
          tone="secondary"
        />
      </div>

      {/* 구독 · 결제 승인 */}
      <section>
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
          구독 · 결제 승인
        </h2>
        <p className="text-xs text-foreground-500 mb-3">
          입금자명과 가입자 이름이 일치하는지 확인 후 승인해주세요. 일치하지 않으면 거절 또는 문의 후 처리하시기 바랍니다.
        </p>
        <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
          {pendingSubs.length === 0 ? (
            <div className="p-6 text-center text-foreground-500 text-sm">
              입금 확인 대기 중인 구독 신청이 없습니다.
            </div>
          ) : (
            pendingSubs.map((sub) => {
              const roleLabel = sub.role === 'owner' ? '학원장' : '일반회원';
              const roleBadge =
                sub.role === 'owner'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-accent-100 text-accent-700';
              const selPlan = planSel[sub.id] || '1month';
              const selPrice = PLANS[sub.role === 'owner' ? 'owner' : 'member'][selPlan];
              return (
                <div key={sub.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                    <i className="ri-bank-card-line text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground-950 text-sm">
                        {subProfiles[sub.user_id]?.full_name || '이름 없음'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadge}`}>{roleLabel}</span>
                      {subProfiles[sub.user_id]?.phone && (
                        <span className="text-xs text-foreground-500">{subProfiles[sub.user_id]?.phone}</span>
                      )}
                    </div>
                    {sub.role === 'owner' && (
                      <div className="text-sm text-foreground-600 mt-0.5">
                        {subProfiles[sub.user_id]?.academy_name || '학원명 미기입'}
                      </div>
                    )}
                    <div className="text-xs text-foreground-500 mt-0.5">
                      신청: {PLAN_LABELS[sub.plan as PlanKey] || '이용권'} ·{' '}
                      {formatPrice(sub.price)}원
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <select
                      value={selPlan}
                      onChange={(e) => setPlanSel((prev) => ({ ...prev, [sub.id]: e.target.value as PlanKey }))}
                      className="px-2 py-1.5 text-xs rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                    >
                      {planKeys.map((key) => (
                        <option key={key} value={key}>
                          {PLAN_LABELS[key]} ({formatPrice(PLANS[sub.role === 'owner' ? 'owner' : 'member'][key])}원)
                        </option>
                      ))}
                    </select>
                    <span className="text-xs font-semibold text-primary-700 whitespace-nowrap">
                      {formatPrice(selPrice)}원
                    </span>
                    <button
                      onClick={() => approveSubscription(sub.id)}
                      disabled={busy}
                      className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                    >
                      입금 확인
                    </button>
                    <button
                      onClick={() => rejectSubscription(sub.id)}
                      disabled={busy}
                      className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                    >
                      거절
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* 환불 요청 */}
      <section>
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">환불 요청</h2>
        <p className="text-xs text-foreground-500 mb-3">
          3개월·1년 이용권 환불 신청입니다. 승인 시 1개월 이용금액을 공제한 나머지 금액을 개인 송금으로 환불해주세요.
        </p>
        <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
          {refundReqs.length === 0 ? (
            <div className="p-6 text-center text-foreground-500 text-sm">
              대기 중인 환불 요청이 없습니다.
            </div>
          ) : (
            refundReqs.map((req) => {
              const roleLabel = req.role === 'owner' ? '학원장' : '일반회원';
              return (
                <div key={req.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                    <i className="ri-refund-2-line text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground-950 text-sm">
                        {refundProfiles[req.user_id]?.full_name || '이름 없음'}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                        {roleLabel}
                      </span>
                      {refundProfiles[req.user_id]?.phone && (
                        <span className="text-xs text-foreground-500">{refundProfiles[req.user_id]?.phone}</span>
                      )}
                    </div>
                    <div className="text-sm text-foreground-600 mt-0.5">
                      {PLAN_LABELS[req.plan as PlanKey] || '이용권'} · 결제 {formatPrice(req.paid_price)}원 ·{' '}
                      <span className="font-semibold text-primary-700">
                        환불 {formatPrice(req.refund_amount)}원
                      </span>
                    </div>
                    {req.reason && (
                      <div className="text-xs text-foreground-500 mt-0.5">사유: {req.reason}</div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => approveRefund(req.id)}
                      disabled={refundBusy === req.id}
                      className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                    >
                      환불 승인
                    </button>
                    <button
                      onClick={() => rejectRefund(req.id)}
                      disabled={refundBusy === req.id}
                      className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                    >
                      거절
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* 문의 및 건의사항 */}
      <section>
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">문의 및 건의사항</h2>
        <p className="text-xs text-foreground-500 mb-3">
          회원이 접수한 문의입니다. 답변을 등록하면 회원이 문의 페이지에서 바로 확인할 수 있어요.
        </p>
        <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
          {inquiries.length === 0 ? (
            <div className="p-6 text-center text-foreground-500 text-sm">
              접수된 문의가 없습니다.
            </div>
          ) : (
            inquiries.map((inq) => {
              const isAnswered = inq.status === 'answered';
              const isSuggestion = inq.category === 'suggestion';
              return (
                <div key={inq.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 flex-shrink-0">
                      <i className={isSuggestion ? 'ri-lightbulb-line text-lg' : 'ri-question-line text-lg'}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground-950 text-sm">
                          {inqProfiles[inq.user_id]?.full_name || '이름 없음'}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            isSuggestion ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                          }`}
                        >
                          {isSuggestion ? '건의사항' : '문의'}
                        </span>
                        {inqProfiles[inq.user_id]?.phone && (
                          <span className="text-xs text-foreground-500">{inqProfiles[inq.user_id]?.phone}</span>
                        )}
                        <span className="text-xs text-foreground-400">
                          {new Date(inq.created_at).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                      <div className="font-medium text-foreground-900 text-sm mt-1">{inq.title}</div>
                      <p className="text-sm text-foreground-600 mt-1 whitespace-pre-wrap break-words">
                        {inq.content}
                      </p>

                      {isAnswered && inq.response ? (
                        <div className="mt-3 rounded-lg bg-background-50 border border-background-200 p-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <i className="ri-reply-line text-primary-600"></i>
                            <span className="text-xs font-semibold text-foreground-900">답변 완료</span>
                          </div>
                          <p className="text-sm text-foreground-700 whitespace-pre-wrap break-words">
                            {inq.response}
                          </p>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <textarea
                            value={inqResponses[inq.id] || ''}
                            onChange={(e) =>
                              setInqResponses((prev) => ({ ...prev, [inq.id]: e.target.value }))
                            }
                            rows={2}
                            placeholder="답변 내용을 입력하세요"
                            className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                          />
                          <div className="flex justify-end mt-2">
                            <button
                              onClick={() => respondInquiry(inq.id)}
                              disabled={inqBusy === inq.id}
                              className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                            >
                              {inqBusy === inq.id ? '등록 중...' : '답변 등록'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* 개인회원 관리 */}
      <MemberManager />

      {/* 학원장 관리 */}
      <OwnerManager />

      {/* 학생 가입 승인 */}
      <section>
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">학생 가입 승인</h2>
        <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
          {pendingStudents.length === 0 ? (
            <div className="p-6 text-center text-foreground-500 text-sm">
              승인 대기 중인 학생 가입 요청이 없습니다.
            </div>
          ) : (
            pendingStudents.map((student) => (
              <div key={student.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground-950 text-sm">
                      {student.full_name || '이름 없음'}
                    </span>
                    {student.grade && (
                      <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
                        {student.grade}
                      </span>
                    )}
                    <span className="text-xs text-foreground-500">{student.address || '지역 미기입'}</span>
                  </div>
                  <div className="text-sm text-foreground-600 mt-0.5">
                    {student.academy_name || '학원명 미기입'}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => approveStudent(student.id)}
                    disabled={busy}
                    className="whitespace-nowrap bg-accent-500 hover:bg-accent-600 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                  >
                    승인
                  </button>
                  <button
                    onClick={() => reject(student.id)}
                    disabled={busy}
                    className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                  >
                    거절
                  </button>
                  <button
                    onClick={() => forceDelete(student.id)}
                    disabled={busy}
                    className="whitespace-nowrap text-red-600 hover:bg-red-50 text-xs font-semibold px-3 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                  >
                    강제탈퇴
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* 정원 증설 요청 */}
      <section>
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">정원 증설 요청</h2>
        <p className="text-xs text-foreground-500 mb-3">
          증설 요금은 +100명당 {formatPrice(CAPACITY_EXPANSION_PRICE)}원입니다. 입금 계좌({BANK_INFO.bank}{' '}
          {BANK_INFO.account} · {BANK_INFO.holder})로 입금자명 확인 후 승인해주세요.
        </p>
        <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
          {capacityRequests.length === 0 ? (
            <div className="p-6 text-center text-foreground-500 text-sm">
              대기 중인 정원 증설 요청이 없습니다.
            </div>
          ) : (
            capacityRequests.map((req) => (
              <div key={req.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 flex-shrink-0">
                  <i className="ri-add-circle-line text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-foreground-950 text-sm">
                    {req.academies?.name || '학원명 미기입'}
                  </div>
                  <div className="text-sm text-primary-700 font-medium mt-0.5">
                    정원 +{req.requested_slots}명 증설 요청
                  </div>
                  <div className="text-xs font-semibold text-primary-700 mt-0.5">
                    증설 요금 {formatPrice(req.price ?? CAPACITY_EXPANSION_PRICE)}원
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5">
                    {new Date(req.created_at).toLocaleDateString('ko-KR')}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => approveExpansion(req.id)}
                    disabled={busy}
                    className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                  >
                    입금 확인
                  </button>
                  <button
                    onClick={() => rejectExpansion(req.id)}
                    disabled={busy}
                    className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                  >
                    거절
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* 학원 목록 */}
      <section>
        <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">학원 목록</h2>
        <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
          {academies.length === 0 ? (
            <div className="p-8 text-center text-foreground-500 text-sm">
              아직 등록된 학원이 없습니다.
            </div>
          ) : (
            academies.map((academy) => (
              <div
                key={academy.id}
                onClick={() => isMasterOperator && navigate(`/academy/${academy.id}`)}
                className={`p-4 flex items-center gap-4 ${
                  isMasterOperator
                    ? 'cursor-pointer hover:bg-background-50 transition-colors'
                    : ''
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                  <i className="ri-building-2-line text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-foreground-950 text-sm">{academy.name}</div>
                  <div className="text-xs text-foreground-500">{academy.address || '주소 미기입'}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-foreground-900">
                    {studentCounts[academy.id] || 0} / {academy.max_students || 100}명
                  </div>
                  <div className="text-xs text-foreground-500">학생 / 정원</div>
                </div>
                {isMasterOperator && (
                  <div className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 shrink-0">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {isMasterOperator && <AdminPasswordReset />}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: string;
  label: string;
  value: string;
  tone: 'primary' | 'accent' | 'secondary';
}) {
  const iconTone =
    tone === 'primary'
      ? 'bg-primary-100 text-primary-700'
      : tone === 'accent'
        ? 'bg-accent-100 text-accent-700'
        : 'bg-secondary-100 text-secondary-700';

  return (
    <div className="bg-white rounded-xl border border-background-200 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 flex items-center justify-center rounded-lg ${iconTone}`}>
        <i className={`${icon} text-xl`}></i>
      </div>
      <div>
        <div className="text-2xl font-bold text-foreground-950">{value}</div>
        <div className="text-sm text-foreground-500">{label}</div>
      </div>
    </div>
  );
}